import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getUserDocId } from "../../utils/userDocId"; // ✅ ensure this import is here

export default function AdminComplianceTable() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);

  const [summary, setSummary] = useState({
    mandatory: 0,
    training: 0,
    forms: 0,
    references: 0,
    overall: 0,
  });

  /* ---- compliance schema ---- */
  const requiredDocs = {
    mandatory: [
      { id: "dbs", name: "DBS Certificate", renewal: "Annual" },
      { id: "rtw", name: "Right to Work", renewal: "As Required" },
      { id: "passport", name: "Passport", renewal: "As Required" },
      { id: "safeAdults", name: "Safeguarding for Adults", renewal: "Annual" },
      { id: "safeChildren", name: "Safeguarding for Children", renewal: "Annual" },
    ],
    training: [
      { id: "careCert", name: "Care Certificate", renewal: "Annual" },
      { id: "movingHandling", name: "Moving & Handling", renewal: "Annual" },
      { id: "firstAid", name: "First Aid / CPR", renewal: "Annual" },
      { id: "infectionControl", name: "Infection Control", renewal: "Annual" },
      { id: "medAdmin", name: "Medication Administration", renewal: "Annual" },
      { id: "safeguardingAdults", name: "Safeguarding Adults", renewal: "Annual" },
    ],
    forms: [
      { id: "staffRegistration", name: "Staffing Registration Form" },
      { id: "newStarterPayroll", name: "New Starter Payroll Form" },
    ],
    references: [
      { id: "reference1", name: "Reference Request 1" },
      { id: "reference2", name: "Reference Request 2" },
      { id: "cvUpload", name: "CV Upload", renewal: "As Required" },
    ],
  };

  useEffect(() => {
    const fetchUsers = async () => {
      const snap = await getDocs(collection(db, "users"));
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setUsers(data);
    };
    fetchUsers();
  }, []);

  /* ---- calculate progress ---- */
  const calcProgress = (req, docsMap) => {
    const countVerified = (list) =>
      list.filter((d) => {
        const rec = docsMap.find((x) => x.id === d.id);
        return rec && (rec.verified || rec.status === "verified" || rec.status === "uploaded");
      }).length;

    const pct = (ok, total) => (total > 0 ? Math.round((ok / total) * 100) : 0);

    const mandatoryOk = countVerified(requiredDocs.mandatory);
    const trainingOk = countVerified(requiredDocs.training);
    const formsOk = countVerified(requiredDocs.forms);
    const refsOk = countVerified(requiredDocs.references);

    const summary = {
      mandatory: pct(mandatoryOk, requiredDocs.mandatory.length),
      training: pct(trainingOk, requiredDocs.training.length),
      forms: pct(formsOk, requiredDocs.forms.length),
      references: pct(refsOk, requiredDocs.references.length),
    };
    summary.overall = Math.round(
      (summary.mandatory + summary.training + summary.forms + summary.references) / 4
    );
    return summary;
  };

  /* ---- fetch compliance docs ---- */
  const fetchUserDocs = async (userObj) => {
    if (!userObj?.email) return;
    setLoading(true);
    try {
      const snap = await getDocs(
        collection(db, "users", getUserDocId(userObj), "complianceDocs")
      );

      const existing = {};
      snap.forEach((d) => (existing[d.id] = d.data()));

      const merged = [
        ...requiredDocs.mandatory.map((r) => ({ group: "Mandatory", ...r })),
        ...requiredDocs.training.map((r) => ({ group: "Training", ...r })),
        ...requiredDocs.forms.map((r) => ({ group: "Forms", ...r })),
        ...requiredDocs.references.map((r) => ({ group: "References & CV", ...r })),
      ].map((r) => ({
        ...r,
        ...existing[r.id],
      }));

      setDocs(merged);
      setSummary(calcProgress(requiredDocs, merged));
    } finally {
      setLoading(false);
    }
  };

  /* ---- select user ---- */
  const handleSelectUser = (user) => {
    setSelectedUser(user);
    fetchUserDocs(user); // ✅ pass the whole user object, not just ID
  };

  /* ---- verify / undo ---- */
  const markAsVerified = async (userObj, d) => {
    await setDoc(
      doc(db, "users", getUserDocId(userObj), "complianceDocs", d.id),
      {
        name: d.name,
        renewal: d.renewal || "As Required",
        verified: true,
        status: "verified",
        verifiedAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
      },
      { merge: true }
    );
    await fetchUserDocs(userObj);
await updateComplianceSummary(userObj, docs);

  };

  const undoVerify = async (userObj, d) => {
    await updateDoc(
      doc(db, "users", getUserDocId(userObj), "complianceDocs", d.id),
      {
        verified: false,
        status: d.fileUrl ? "uploaded" : "missing",
        lastUpdated: serverTimestamp(),
      }
    );
    await fetchUserDocs(userObj);
	await updateComplianceSummary(userObj, docs);

  };

  const ProgressBar = ({ value }) => (
    <div
      style={{
        background: "#e5e7eb",
        borderRadius: 10,
        height: 8,
        width: "100%",
      }}
    >
      <div
        style={{
          width: `${value}%`,
          height: "100%",
          background: "#1e3a8a",
          borderRadius: 10,
          transition: "width .3s",
        }}
      />
    </div>
  );
  
  
  // 🔹 Update staff compliance summary in Firestore after any change
const updateComplianceSummary = async (userObj, docsData) => {
  // Separate categories
  const mandatory = docsData.filter((d) => d.group === "Mandatory");
  const training = docsData.filter((d) => d.group === "Training");
  const forms = docsData.filter((d) => d.group === "Forms");
  const refs = docsData.filter((d) => d.group === "References & CV");

  const pct = (ok, total) => (total > 0 ? Math.round((ok / total) * 100) : 0);
  const countOk = (arr) => arr.filter((d) => d.verified || d.status === "verified").length;

  // Calculate progress per category
  const mandatoryPct = pct(countOk(mandatory), mandatory.length);
  const trainingPct = pct(countOk(training), training.length);
  const formsPct = pct(countOk(forms), forms.length);
  const refsPct = pct(countOk(refs), refs.length);

  // Average overall progress
  const overall = Math.round(
    (mandatoryPct + trainingPct + formsPct + refsPct) / 4
  );

  // Save summary into the user's Firestore document
  await setDoc(
    doc(db, "users", getUserDocId(userObj)),
    {
      complianceSummary: {
        mandatory: mandatoryPct,
        training: trainingPct,
        forms: formsPct,
        references: refsPct,
        overall: overall,
        lastUpdated: serverTimestamp(),
      },
    },
    { merge: true }
  );

  console.log(`✅ Updated summary for ${userObj.email}:`, {
    mandatoryPct,
    trainingPct,
    formsPct,
    refsPct,
    overall,
  });
};

  
  

  /* ---- render ---- */
  return (
    <div style={{ padding: 20 }}>
      <h2 style={{ marginBottom: 10 }}>Admin Compliance Overview</h2>

      {/* select staff */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ marginRight: 8 }}>Select Staff:</label>
        <select
          value={selectedUser?.id || ""}
          onChange={(e) => {
            const u = users.find((x) => x.id === e.target.value);
            if (u) handleSelectUser(u);
          }}
          style={{ padding: 6, borderRadius: 6, border: "1px solid #d1d5db" }}
        >
          <option value="">-- Choose a user --</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name || u.email || u.id}
            </option>
          ))}
        </select>
      </div>

      {selectedUser && (
        <>
          <div
            style={{
              maxWidth: 700,
              marginBottom: 20,
              background: "white",
              padding: 20,
              borderRadius: 12,
              boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            }}
          >
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>
              {selectedUser.name || selectedUser.email}'s Compliance Progress
            </h3>

            <div style={{ fontSize: 13, color: "#374151", marginBottom: 6 }}>
              Mandatory: {summary.mandatory}% <ProgressBar value={summary.mandatory} />
            </div>
            <div style={{ fontSize: 13, color: "#374151", marginBottom: 6 }}>
              Training: {summary.training}% <ProgressBar value={summary.training} />
            </div>
            <div style={{ fontSize: 13, color: "#374151", marginBottom: 6 }}>
              Forms: {summary.forms}% <ProgressBar value={summary.forms} />
            </div>
            <div style={{ fontSize: 13, color: "#374151", marginBottom: 6 }}>
              References & CV: {summary.references}%{" "}
              <ProgressBar value={summary.references} />
            </div>
            <div style={{ fontWeight: 600, color: "#1e3a8a", marginTop: 10 }}>
              Overall: {summary.overall}% <ProgressBar value={summary.overall} />
            </div>
          </div>

          {/* ---- document table ---- */}
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              overflow: "hidden",
              background: "white",
            }}
          >
		  
		  <div style={{ overflowX: "auto", width: "100%" }}>
		  
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px", }}>
              <thead style={{ background: "#f3f4f6", borderBottom: "2px solid #e5e7eb", }}>
                <tr>
                  <th style={{ padding: "12px 8px", textAlign: "left", fontWeight: 600 }}>Group</th>
                  <th style={{ padding: "12px 8px", textAlign: "left", fontWeight: 600 }}>Document</th>
                  <th style={{ padding: "12px 8px", fontWeight: 600 }}>Renewal</th>
                  <th style={{ padding: 10 }}>Status</th>
                  <th style={{ padding: 10 }}>File</th>
                  <th style={{ padding: 10 }}>Uploaded</th>
                  <th style={{ padding: 10 }}>Verified</th>
                  <th style={{ padding: 10 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" style={{ padding: 16, textAlign: "center" }}>
                      Loading…
                    </td>
                  </tr>
                ) : (
                  docs.map((d) => {
                    const statusColor =
					  d.verified
						? "#16a34a"
						: ["uploaded", "pending", "in_review"].includes(d.status)
						? "#1e3a8a"
						: "#b91c1c";

					const statusLabel = d.verified
					  ? "Verified"
					  : d.status
					  ? d.status.charAt(0).toUpperCase() + d.status.slice(1)
					  : "Missing";

                    return (
                      <tr key={d.id} style={{ borderTop: "1px solid #e5e7eb" }}>
                        <td style={{ padding: 10, color: "#6b7280", fontSize: 12 }}>
                          {d.group}
                        </td>
                        <td style={{ padding: 10, fontWeight: 600 }}>{d.name || d.id}</td>
                        <td style={{ textAlign: "center" }}>{d.renewal || "—"}</td>
                        <td style={{ textAlign: "center", color: statusColor }}>
							{statusLabel}
						</td>

                        <td style={{ textAlign: "center" }}>
                          {d.fileUrl ? (
                            <a
                              href={d.fileUrl}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                color: "#1e3a8a",
                                textDecoration: "underline",
                              }}
                            >
                              View
                            </a>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {d.uploadedAt?.seconds
                            ? new Date(d.uploadedAt.seconds * 1000).toLocaleDateString()
                            : "—"}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {d.verifiedAt?.seconds
                            ? new Date(d.verifiedAt.seconds * 1000).toLocaleDateString()
                            : d.verified
                            ? "Yes"
                            : "No"}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {d.verified ? (
                            <button
                              onClick={() => undoVerify(selectedUser, d)}
                              style={{
                                background: "white",
                                color: "#1f2937",
                                border: "1px solid #d1d5db",
                                borderRadius: 6,
                                padding: "6px 10px",
                                fontSize: 12,
                                cursor: "pointer",
                              }}
                            >
                              Undo verify
                            </button>
                          ) : (
                            <button
                              onClick={() => markAsVerified(selectedUser, d)}
                              style={{
                                background: "#16a34a",
                                color: "white",
                                border: "none",
                                borderRadius: 6,
                                padding: "6px 10px",
                                fontSize: 12,
                                cursor: "pointer",
                              }}
                            >
                              Mark verified
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
			</div>
          </div>
        </>
      )}
    </div>
  );
}
